import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger, AlertDialogDescription } from "@/components/ui/alert-dialog";
import { brl, fmtTime, fmtDate, toISODateInput } from "@/lib/format";
import { formatAppointmentServices } from "@/lib/appointment";
import { can, useMe } from "@/lib/permissions";
import { Plus, Trash2, Route, ChevronLeft, ChevronRight, Wallet, CircleCheck, CircleX, Loader2, Copy, MessageCircle, CalendarClock, ClipboardCheck, Pencil } from "lucide-react";
import CepField from "@/components/CepField";
import { TollDialog } from "@/components/TollDialog";

const STATUS_LABEL = { scheduled: "Agendado", completed: "Concluído", canceled: "Cancelado", no_show: "Faltou" };

export default function Appointments() {
  const [sp] = useSearchParams();
  const [day, setDay] = useState(() => {
    const q = sp.get("date");
    if (q) { const d = new Date(q + "T00:00:00"); if (!isNaN(d)) return d; }
    return new Date();
  });
  const [list, setList] = useState([]);
  const [services, setServices] = useState([]);
  const [pros, setPros] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(defaultForm());
  const [computingKm, setComputingKm] = useState(false);
  const [payOpen, setPayOpen] = useState(null); // appointment id
  const [pixData, setPixData] = useState(null);
  const [creatingPayment, setCreatingPayment] = useState(false);
  const [issueInvoice, setIssueInvoice] = useState(false);
  const [rescheduleFor, setRescheduleFor] = useState(null); // appointment being rescheduled
  const [rescheduleAt, setRescheduleAt] = useState(""); // yyyy-MM-ddTHH:mm
  const [rescheduling, setRescheduling] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [routeSlots, setRouteSlots] = useState([]);
  const [tollFor, setTollFor] = useState(null);
  const [tollFinishing, setTollFinishing] = useState(false);
  const nav = useNavigate();
  const me = useMe();
  const isProLogin = !!me?.professional_id;
  const canSeeCommission = !isProLogin || (me?.permissions || []).includes("reports.view_commission");
  const isOwner = me?.role === "owner" || me?.role === "platform_admin" || !!me?.is_platform_admin;

  function defaultForm() {
    return { client_id: "", client_name: "", client_phone: "", client_email: "", client_cpf_cnpj: "", client_address: "", professional_id: "", services: [{ service_id: "", quantity: 1 }], time: "09:00", km: "", notes: "" };
  }

  const loadRefs = async () => {
    const [s, p, c] = await Promise.all([api.get("/services"), api.get("/professionals"), api.get("/clients")]);
    setServices(s.data.filter((x) => x.active)); setPros(p.data.filter((x) => x.active)); setClients(c.data);
  };
  const loadDayReqRef = useRef(0);
  const loadDay = async () => {
    const reqId = ++loadDayReqRef.current;
    const s = new Date(day); s.setHours(0, 0, 0, 0);
    const e = new Date(day); e.setHours(23, 59, 59, 999);
    const { data } = await api.get("/appointments", { params: { start: s.toISOString(), end: e.toISOString() } });
    // Ignore stale responses: only the newest in-flight request may commit.
    if (reqId === loadDayReqRef.current) setList(data);
  };
  useEffect(() => { loadRefs(); }, []);
  useEffect(() => { loadDay(); }, [day]);
  // Pro-logins: as soon as they open the Agenda page, mark all new-appointment
  // notifications as seen so the bell resets. Fire-and-forget.
  useEffect(() => {
    if (!isProLogin) return;
    api.post("/notifications/mark-seen").catch(() => {});
  }, [isProLogin]);

  // Route-optimization hint: when the admin fills in professional + address in
  // the create/edit dialog, ask the backend which slots on this day are near
  // other appointments already booked for the same pro. The user can then
  // one-click apply a recommended time to `form.time`.
  useEffect(() => {
    if (!open || editingId) { setRouteSlots([]); return; }  // reschedule/edit use their own dialogs
    const svcId = (form.services || []).find((l) => l.service_id)?.service_id;
    if (!form.professional_id || !svcId || !form.client_address || form.client_address.trim().length < 6) {
      setRouteSlots([]); return;
    }
    const ctrl = new AbortController();
    (async () => {
      try {
        const yyyymmdd = day.toISOString().slice(0, 10);
        const { data } = await api.get("/availability", {
          params: { professional_id: form.professional_id, service_id: svcId, day: yyyymmdd, client_address: form.client_address.trim() },
          signal: ctrl.signal,
        });
        const rec = (data.slots || []).filter((s) => s.available && s.recommended);
        setRouteSlots(rec.slice(0, 6));
      } catch { /* ignore */ }
    })();
    return () => ctrl.abort();
  }, [open, editingId, day, form.professional_id, form.client_address, form.services]);

  // Sum the durations/prices of all selected service lines. The professional's
  // time is blocked for the full combined duration on the backend.
  const serviceLines = useMemo(() => {
    return (form.services || []).map((l) => {
      const s = services.find((x) => x.id === l.service_id);
      if (!s) return { ...l, service: null, subtotal: 0, minutes: 0 };
      const qty = Math.max(1, Number(l.quantity) || 1);
      return { ...l, quantity: qty, service: s, subtotal: Number(s.price) * qty, minutes: Number(s.duration_minutes) * qty };
    });
  }, [form.services, services]);
  const totalMinutes = serviceLines.reduce((n, l) => n + l.minutes, 0);
  const totalServicesPrice = serviceLines.reduce((n, l) => n + l.subtotal, 0);
  // Displacement uses the first service that has it enabled.
  const dispService = serviceLines.map((l) => l.service).find((s) => s?.has_displacement);
  const hasDisp = !!dispService;

  const addServiceLine = () => setForm((f) => ({ ...f, services: [...(f.services || []), { service_id: "", quantity: 1 }] }));
  const removeServiceLine = (idx) => setForm((f) => ({ ...f, services: f.services.filter((_, i) => i !== idx) }));
  const updateServiceLine = (idx, patch) => setForm((f) => ({ ...f, services: f.services.map((l, i) => i === idx ? { ...l, ...patch } : l) }));

  const setF = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const selectExistingClient = (id) => {
    if (id === "__new__") { setForm((f) => ({ ...f, client_id: "", client_name: "", client_phone: "", client_email: "", client_cpf_cnpj: "", client_address: "" })); return; }
    const c = clients.find((x) => x.id === id);
    if (!c) return;
    setForm((f) => ({ ...f, client_id: c.id, client_name: c.name, client_phone: c.phone || "", client_email: c.email || "", client_cpf_cnpj: c.cpf_cnpj || "", client_address: c.address || "" }));
  };

  const computeKm = async () => {
    if (!form.client_address) { toast.error("Informe o endereço do cliente"); return; }
    setComputingKm(true);
    try {
      const { data } = await api.post("/geocode/distance", { address: form.client_address });
      setForm((f) => ({ ...f, km: String(data.km) }));
      toast.success(`Distância ida+volta: ${data.km} km`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Não foi possível calcular"); }
    finally { setComputingKm(false); }
  };

  const openNew = () => { setEditingId(null); setForm(defaultForm()); setOpen(true); };

  const openEdit = (a) => {
    setEditingId(a.id);
    const d = new Date(a.scheduled_at);
    const pad = (n) => String(n).padStart(2, "0");
    const svcLines = (a.services && a.services.length > 0)
      ? a.services.map((l) => ({ service_id: l.service_id, quantity: l.quantity || 1 }))
      : [{ service_id: a.service_id, quantity: 1 }];
    setForm({
      client_id: a.client_id || "",
      client_name: a.client_name || "",
      client_phone: "", client_email: "", client_cpf_cnpj: "",
      client_address: a.client_address || "",
      professional_id: a.professional_id,
      services: svcLines,
      time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
      km: a.km != null ? String(a.km) : "",
      notes: a.notes || "",
    });
    setDay(d);
    setOpen(true);
  };

  const save = async () => {
    const validLines = (form.services || []).filter((l) => l.service_id);
    if (validLines.length === 0 || !form.professional_id) { toast.error("Selecione ao menos um serviço e o profissional"); return; }
    if (editingId) {
      // Edit — only send fields the backend PATCH accepts. Scheduled_at is
      // changed via the Reschedule button (dedicated flow with its own lock).
      const payload = {
        services: validLines.map((l) => ({ service_id: l.service_id, quantity: Math.max(1, Number(l.quantity) || 1) })),
        client_address: form.client_address || "",
        notes: form.notes || "",
        km: hasDisp && form.km !== "" ? Number(form.km) : null,
      };
      // Only include client_id when the admin actually picked a different one
      // (or picked "+ New client" and typed the fields inline).
      if (form.client_id) {
        payload.client_id = form.client_id;
      } else if (form.client_name) {
        // Inline swap without creating a client — just imprint the new fields.
        payload.client_name = form.client_name;
        payload.client_phone = form.client_phone || "";
        payload.client_email = form.client_email || "";
        payload.client_cpf_cnpj = form.client_cpf_cnpj || "";
      }
      // Owner-only: allow swapping the professional inline.
      if (isOwner && form.professional_id) {
        payload.professional_id = form.professional_id;
      }
      try {
        await api.patch(`/appointments/${editingId}`, payload);
        toast.success("Agendamento atualizado");
        setOpen(false); setEditingId(null); loadDay();
      } catch (e) {
        // Owner override: se a edição gera conflito de horário/serviço, pergunta
        // se quer salvar mesmo assim (registra `force_created=true`).
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail || "";
        const conflictable = status === 400 || status === 409;
        if (conflictable && isOwner) {
          const proceed = window.confirm(`${detail}\n\nDeseja salvar mesmo assim? O agendamento será registrado ignorando o conflito.`);
          if (proceed) {
            try {
              await api.patch(`/appointments/${editingId}`, { ...payload, force: true });
              toast.success("Agendamento atualizado (com conflito registrado)");
              setOpen(false); setEditingId(null); loadDay();
              return;
            } catch (e2) { toast.error(errMsg(e2, "Falha ao atualizar")); return; }
          }
        }
        toast.error(errMsg(e, "Falha ao atualizar"));
      }
      return;
    }
    if (!form.client_id && !form.client_name) { toast.error("Selecione ou cadastre o cliente"); return; }
    const [hh, mm] = form.time.split(":");
    const dt = new Date(day); dt.setHours(Number(hh), Number(mm), 0, 0);
    const payload = {
      client_id: form.client_id || null,
      client_name: form.client_name || null,
      client_phone: form.client_phone || null,
      client_email: form.client_email || null,
      client_cpf_cnpj: form.client_cpf_cnpj || null,
      professional_id: form.professional_id,
      services: validLines.map((l) => ({ service_id: l.service_id, quantity: Math.max(1, Number(l.quantity) || 1) })),
      scheduled_at: dt.toISOString(),
      km: hasDisp && form.km !== "" ? Number(form.km) : null,
      client_address: form.client_address || null,
      notes: form.notes,
    };
    try {
      await api.post("/appointments", payload);
      toast.success("Agendamento criado");
      setOpen(false); loadDay();
    } catch (e) {
      // Owners can override past-date/overlap conflicts after an explicit
      // confirmation dialog. Any other error goes straight to a toast.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || "";
      const conflictable = status === 400 || status === 409;
      const isOwner = me?.role === "owner" || me?.role === "platform_admin" || me?.is_platform_admin;
      if (conflictable && isOwner) {
        const proceed = window.confirm(`${detail}\n\nDeseja prosseguir mesmo assim? Esta ação vai criar o agendamento ignorando o conflito de horário.`);
        if (proceed) {
          try {
            await api.post("/appointments", { ...payload, force: true });
            toast.success("Agendamento criado (com conflito registrado)");
            setOpen(false); loadDay();
            return;
          } catch (e2) { toast.error(errMsg(e2, "Falha ao agendar")); return; }
        }
      }
      toast.error(errMsg(e, "Falha ao agendar"));
    }
  };

  const canEditToll = (a) => isOwner || can("appointments.write") || (me?.professional_id && me.professional_id === a.professional_id);
  const openToll = (a, finishing) => { setTollFinishing(finishing); setTollFor(a); };
  const changeStatus = async (id, status) => {
    const a = list.find((x) => x.id === id);
    if (status === "completed" && a && canEditToll(a)) { openToll(a, true); return; }
    try { await api.patch(`/appointments/${id}/status`, { status }); toast.success("Status atualizado"); loadDay(); }
    catch { toast.error("Falha ao atualizar"); }
  };
  const remove = async (id) => { try { await api.delete(`/appointments/${id}`); toast.success("Removido"); loadDay(); } catch { toast.error("Falha"); } };

  const sendWhatsReminder = async (a) => {
    const [client] = await Promise.all([api.get(`/clients`)]).then((rs) => [rs[0].data.find((c) => c.id === a.client_id)]);
    const phone = (client?.phone || "").replace(/\D/g, "");
    if (!phone) { toast.error("Cliente sem telefone cadastrado"); return; }
    const when = new Date(a.scheduled_at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    const msg = encodeURIComponent(
      `Olá, ${a.client_name}! Lembrete do seu agendamento:\n\n` +
      `📅 ${when}\n` +
      `💇 ${formatAppointmentServices(a)}\n` +
      `👤 com ${a.professional_name}\n` +
      `💰 ${new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(a.price)}\n\n` +
      `Confirma sua presença? Obrigado!`
    );
    const url = phone.length >= 10 ? `https://wa.me/55${phone}?text=${msg}` : `https://wa.me/?text=${msg}`;
    window.open(url, "_blank");
  };

  const openReschedule = (a) => {
    setRescheduleFor(a);
    const d = new Date(a.scheduled_at);
    const pad = (n) => String(n).padStart(2, "0");
    setRescheduleAt(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
  };
  const confirmReschedule = async () => {
    if (!rescheduleFor) return;
    setRescheduling(true);
    try {
      const iso = new Date(rescheduleAt).toISOString();
      await api.patch(`/appointments/${rescheduleFor.id}/reschedule`, { scheduled_at: iso });
      toast.success("Horário remarcado");
      setRescheduleFor(null);
      loadDay();
    } catch (e) {
      // Owner override: allow remarcar em cima de outro agendamento / horário
      // bloqueado, com confirmação explícita.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || "";
      const conflictable = status === 400 || status === 409;
      const isOwner = me?.role === "owner" || me?.role === "platform_admin" || me?.is_platform_admin;
      if (conflictable && isOwner) {
        const proceed = window.confirm(`${detail}\n\nDeseja remarcar mesmo assim? O agendamento será registrado ignorando o conflito.`);
        if (proceed) {
          try {
            const iso = new Date(rescheduleAt).toISOString();
            await api.patch(`/appointments/${rescheduleFor.id}/reschedule`, { scheduled_at: iso, force: true });
            toast.success("Horário remarcado (com conflito registrado)");
            setRescheduleFor(null);
            loadDay();
            return;
          } catch (e2) { toast.error(errMsg(e2, "Falha ao remarcar")); return; }
          finally { setRescheduling(false); }
        }
      }
      toast.error(typeof detail === "string" ? detail : "Horário indisponível");
    } finally { setRescheduling(false); }
  };

  const startPayment = async (appt, billing_type) => {
    setCreatingPayment(true); setPixData(null);
    try {
      const { data } = await api.post(`/appointments/${appt.id}/payment`, {
        billing_type,
        // Toggle "Emitir NF-e junto" — quando ligado, o backend chama
        // POST /invoices no Asaas depois de criar a cobrança.
        issue_invoice: !!issueInvoice,
      });
      toast.success("Cobrança criada no Asaas");
      if (data.nfe) {
        if (data.nfe.issued) toast.success("NF-e agendada no Asaas");
        else toast.warning(data.nfe.detail || "NF-e não emitida — verifique no Asaas");
      }
      if (billing_type === "PIX") {
        const pix = await api.get(`/appointments/${appt.id}/payment/pix`);
        setPixData(pix.data);
      } else if (data.invoiceUrl) {
        window.open(data.invoiceUrl, "_blank");
      }
      loadDay();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.provider_error?.errors?.[0]?.description || "Falha ao criar cobrança");
    } finally { setCreatingPayment(false); }
  };

  const shift = (n) => { const d = new Date(day); d.setDate(d.getDate() + n); setDay(d); };

  return (
    <div className="space-y-6 animate-fade-in-up">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight">Agenda</h1>
          <p className="text-sm text-muted-foreground mt-1">Agendamentos do dia selecionado.</p>
        </div>
        <div className="flex gap-2 items-center">
          <Button data-testid="btn-prev-day" variant="outline" size="icon" onClick={() => shift(-1)}><ChevronLeft className="h-4 w-4" /></Button>
          <Input data-testid="input-day" type="date" value={toISODateInput(day)} onChange={(e) => setDay(new Date(e.target.value + "T00:00:00"))} className="h-11 w-44" />
          <Button data-testid="btn-next-day" variant="outline" size="icon" onClick={() => shift(1)}><ChevronRight className="h-4 w-4" /></Button>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditingId(null); }}>
            <DialogTrigger asChild><Button data-testid="btn-new-appt" onClick={openNew} className="h-11" disabled={!can("appointments.write")}><Plus className="h-4 w-4 mr-2" /> Novo</Button></DialogTrigger>
            <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingId ? "Editar agendamento" : "Novo agendamento"}</DialogTitle>
                <DialogDescription>
                  {editingId
                    ? (isOwner
                        ? "Como administrador você pode trocar cliente, profissional, serviços, endereço, km e observações. Para trocar o horário use o botão Remarcar."
                        : "Altere cliente, serviços, endereço, km e observações. Para trocar o horário use o botão Remarcar.")
                    : "Preencha os dados para criar um agendamento. O horário fica bloqueado automaticamente."}
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4">
                {editingId && (
                  <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                    Horário fixo — para mudar, use o botão <strong>Remarcar</strong>. Cliente e serviços podem ser alterados aqui.
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Cliente {editingId && <span className="text-xs text-muted-foreground font-normal">(pode ser trocado)</span>}</Label>
                  <Select value={form.client_id || "__new__"} onValueChange={selectExistingClient}>
                    <SelectTrigger data-testid="select-client"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__new__">+ Novo cliente</SelectItem>
                      {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {!form.client_id && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2 space-y-2"><Label>Nome</Label><Input data-testid="input-appt-client-name" value={form.client_name} onChange={(e) => setF("client_name")(e.target.value)} /></div>
                    <div className="space-y-2"><Label>Telefone</Label><Input data-testid="input-appt-client-phone" value={form.client_phone} onChange={(e) => setF("client_phone")(e.target.value)} /></div>
                    <div className="space-y-2"><Label>CPF/CNPJ</Label><Input data-testid="input-appt-client-cpf" value={form.client_cpf_cnpj} onChange={(e) => setF("client_cpf_cnpj")(e.target.value)} /></div>
                  </div>
                )}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Serviços</Label>
                    <span className="text-xs text-muted-foreground">Total: <strong className="text-foreground">{totalMinutes} min</strong> · {brl(totalServicesPrice)}</span>
                  </div>
                  <div className="space-y-2">
                    {form.services.map((line, idx) => {
                      const s = services.find((x) => x.id === line.service_id);
                      return (
                        <div key={idx} className="grid grid-cols-12 gap-2 items-end" data-testid={`service-line-${idx}`}>
                          <div className="col-span-8">
                            <Select value={line.service_id} onValueChange={(v) => updateServiceLine(idx, { service_id: v })}>
                              <SelectTrigger data-testid={`select-service-${idx}`} className="h-11"><SelectValue placeholder="Selecione o serviço" /></SelectTrigger>
                              <SelectContent>{services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} — {s.duration_minutes}min · {brl(s.price)}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                          <div className="col-span-3">
                            <Input
                              data-testid={`input-qty-${idx}`}
                              type="number"
                              min="1"
                              step="1"
                              value={line.quantity}
                              onChange={(e) => updateServiceLine(idx, { quantity: Math.max(1, parseInt(e.target.value || "1", 10)) })}
                              className="h-11"
                              placeholder="Qtd"
                            />
                          </div>
                          <div className="col-span-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-11 w-11"
                              data-testid={`btn-remove-service-${idx}`}
                              onClick={() => removeServiceLine(idx)}
                              disabled={form.services.length <= 1}
                              title="Remover serviço"
                            ><Trash2 className="h-4 w-4" /></Button>
                          </div>
                          {s && (
                            <div className="col-span-12 text-xs text-muted-foreground -mt-1 pl-1">
                              {line.quantity}× {s.duration_minutes} min = <strong className="text-foreground">{line.quantity * s.duration_minutes} min</strong> · {brl(line.quantity * s.price)}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={addServiceLine} data-testid="btn-add-service" className="h-9"><Plus className="h-3 w-3 mr-1" />Adicionar serviço</Button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Profissional {editingId && isOwner && <span className="text-xs text-muted-foreground font-normal">(admin pode trocar)</span>}</Label>
                    <Select value={form.professional_id} onValueChange={setF("professional_id")} disabled={!!editingId && !isOwner}>
                      <SelectTrigger data-testid="select-pro"><SelectValue placeholder="Selecione" /></SelectTrigger>
                      <SelectContent>{pros.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Horário</Label><Input data-testid="input-appt-time" type="time" value={form.time} onChange={(e) => setF("time")(e.target.value)} className="h-11" disabled={!!editingId} /></div>
                </div>

                {hasDisp && (
                  <div className="surface-muted p-4 space-y-3">
                    <div className="text-sm font-medium flex items-center gap-2"><Route className="h-4 w-4" /> Deslocamento — {brl(dispService.price_per_km)}/km (ida+volta)</div>
                    <div className="space-y-2">
                      <Label>Endereço do cliente</Label>
                      <CepField onFound={(line) => setF("client_address")(line)} />
                      <Textarea data-testid="input-client-address" rows={2} value={form.client_address} onChange={(e) => setF("client_address")(e.target.value)} placeholder="Rua, número, bairro, cidade, UF" />
                    </div>
                    {routeSlots.length > 0 && (
                      <div className="rounded-lg border border-emerald-500/40 bg-emerald-50 dark:bg-emerald-950/30 p-3 space-y-2" data-testid="route-hint">
                        <div className="text-xs font-medium text-emerald-800 dark:text-emerald-200 flex items-center gap-1.5">
                          <Route className="h-3.5 w-3.5" /> Rota otimizada — horários próximos a outras visitas do dia
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {routeSlots.map((s) => (
                            <button
                              key={s.time}
                              type="button"
                              data-testid={`btn-route-slot-${s.label}`}
                              onClick={() => setF("time")(s.label)}
                              className="h-8 px-3 rounded-md text-xs font-medium bg-emerald-500 text-white hover:bg-emerald-600 transition-colors"
                              title={`Aproximadamente ${s.nearest_km}km de outro atendimento`}
                            >
                              {s.label} · {s.nearest_km}km
                            </button>
                          ))}
                        </div>
                        <div className="text-[11px] text-emerald-700/80 dark:text-emerald-300/80">Clique num horário para aplicar. Não visível para o cliente.</div>
                      </div>
                    )}
                    <div className="flex gap-2 items-end">
                      <div className="flex-1 space-y-2"><Label>Km (ida+volta)</Label><Input data-testid="input-appt-km" type="number" min="0" step="0.1" value={form.km} onChange={(e) => setF("km")(e.target.value)} /></div>
                      <Button data-testid="btn-calc-km" type="button" variant="outline" onClick={computeKm} disabled={computingKm}>
                        {computingKm ? <Loader2 className="h-4 w-4 animate-spin" /> : "Calcular km"}
                      </Button>
                    </div>
                    {form.km !== "" && !isNaN(Number(form.km)) && (
                      <div className="text-xs text-muted-foreground">Deslocamento: <span className="font-medium text-foreground">{brl(Number(form.km) * (dispService.price_per_km || 0))}</span></div>
                    )}
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Observações para o técnico</Label>
                  <Textarea
                    data-testid="input-appt-notes"
                    rows={4}
                    value={form.notes}
                    onChange={(e) => setF("notes")(e.target.value)}
                    placeholder="Ex.: placa do veículo, ponto de referência, complemento, restrições, o que precisa ser feito no cliente..."
                  />
                  <p className="text-xs text-muted-foreground">Essas informações ficam visíveis para o profissional que irá executar o serviço.</p>
                </div>
              </div>
              <DialogFooter><Button data-testid="btn-save-appt" onClick={save}>{editingId ? "Salvar alterações" : "Salvar"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="surface divide-y divide-border">
        <div className="px-4 py-3 text-sm text-muted-foreground font-medium">{fmtDate(day)} · {list.length} agendamento(s)</div>
        {list.length === 0 && <div className="p-12 text-center text-muted-foreground">Nenhum agendamento neste dia.</div>}
        {list.map((a) => (
          <div key={a.id} data-testid={`appt-${a.id}`} className="p-4 flex flex-wrap items-start gap-4 hover:bg-muted/40 transition-colors">
            <div className="w-16 text-lg font-display font-semibold pt-1">{fmtTime(a.scheduled_at)}</div>
            <div className="flex-1 min-w-0 space-y-1">
              <div className="font-medium truncate">
                {a.client_name}
                {a.force_created && (
                  <span data-testid={`appt-force-badge-${a.id}`} className="ml-2 text-[10px] uppercase tracking-widest text-red-600 border border-red-600/40 bg-red-600/10 rounded px-1.5 py-0.5">
                    Conflito
                  </span>
                )}
              </div>
              <div className="text-xs text-foreground/90 break-words" data-testid={`appt-services-${a.id}`}>
                <span className="font-medium">{formatAppointmentServices(a)}</span>
              </div>
              <div className="text-xs text-muted-foreground">{a.professional_name} · {a.duration_minutes} min</div>
              {a.displacement > 0 && (
                <div className="text-xs text-accent-foreground/90 inline-flex items-center gap-1"><Route className="h-3 w-3" /> {a.km} km · +{brl(a.displacement)}</div>
              )}
              {a.toll > 0 && (
                <div className="text-xs text-accent-foreground/90" data-testid={`appt-toll-${a.id}`}>Pedágio: +{brl(a.toll)}{a.toll_auto ? " (automático)" : ""}</div>
              )}
              {a.client_address && (
                <div className="text-xs text-muted-foreground">📍 {a.client_address}</div>
              )}
              {a.notes && (
                <div data-testid={`appt-notes-${a.id}`} className="mt-2 rounded-md border-l-4 border-accent bg-accent/10 px-3 py-2 text-xs text-foreground whitespace-pre-wrap">
                  <div className="text-[10px] uppercase tracking-widest text-accent-foreground/80 font-semibold mb-0.5">Observações</div>
                  {a.notes}
                </div>
              )}
            </div>
            <div className="text-right pt-1">
              <div className="font-semibold">{brl(a.price)}</div>
              {canSeeCommission && <div className="text-xs text-muted-foreground">Com.: {brl(a.commission)}</div>}
            </div>
            <div className="flex items-center gap-2">
              <Select value={a.status} onValueChange={(v) => changeStatus(a.id, v)} disabled={!can("appointments.change_status")}>
                <SelectTrigger data-testid={`select-status-${a.id}`} className="h-9 w-36"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
              <span className={`text-xs px-2 py-1 rounded border ${a.payment_status === "RECEIVED" || a.payment_status === "CONFIRMED" ? "border-primary/30 text-primary bg-primary/5" : "border-border text-muted-foreground"}`}>
                {a.payment_status || "pendente"}
              </span>
              {canEditToll(a) && a.status !== "canceled" && (
                <Button data-testid={`btn-toll-${a.id}`} variant="outline" size="sm" onClick={() => openToll(a, false)} title="Informar pedágio">Pedágio</Button>
              )}
              <Button data-testid={`btn-whats-${a.id}`} variant="outline" size="sm" onClick={() => sendWhatsReminder(a)} title="Enviar lembrete por WhatsApp"><MessageCircle className="h-4 w-4" /></Button>
              {can("appointments.write") && a.status !== "canceled" && a.status !== "no_show" && (
                <Button data-testid={`btn-edit-appt-${a.id}`} variant="outline" size="sm" onClick={() => openEdit(a)} title="Editar serviços, endereço e observações"><Pencil className="h-4 w-4" /></Button>
              )}
              {can("appointments.write") && a.status !== "canceled" && a.status !== "no_show" && (
                <Button data-testid={`btn-reschedule-${a.id}`} variant="outline" size="sm" onClick={() => openReschedule(a)} title="Remarcar para outro horário"><CalendarClock className="h-4 w-4" /></Button>
              )}
              <Button data-testid={`btn-checklist-${a.id}`} variant={a.has_checklist ? "secondary" : "outline"} size="sm" onClick={() => nav(`/checklist/${a.id}${a.has_checklist ? "?view=1" : ""}`)} title={a.has_checklist ? "Ver check-list" : "Preencher check-list"}>
                <ClipboardCheck className="h-4 w-4" />
              </Button>
              {can("payments.charge") && (
                <Popover open={payOpen === a.id} onOpenChange={(o) => { setPayOpen(o ? a.id : null); if (!o) setPixData(null); }}>
                  <PopoverTrigger asChild>
                    <Button data-testid={`btn-pay-${a.id}`} variant="outline" size="sm"><Wallet className="h-4 w-4 mr-1" /> Cobrar</Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80">
                    <div className="space-y-3">
                      <div className="text-sm font-medium">Gerar cobrança Asaas — {brl(a.price)}</div>
                      <div className="grid grid-cols-3 gap-2">
                        <Button size="sm" variant="secondary" disabled={creatingPayment} onClick={() => startPayment(a, "PIX")}>PIX</Button>
                        <Button size="sm" variant="secondary" disabled={creatingPayment} onClick={() => startPayment(a, "BOLETO")}>Boleto</Button>
                        <Button size="sm" variant="secondary" disabled={creatingPayment} onClick={() => startPayment(a, "CREDIT_CARD")}>Cartão</Button>
                      </div>
                      <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer select-none" data-testid={`toggle-nfe-${a.id}`}>
                        <input
                          type="checkbox"
                          checked={issueInvoice}
                          onChange={(e) => setIssueInvoice(e.target.checked)}
                          className="mt-0.5 h-4 w-4 rounded border-border accent-primary"
                        />
                        <span>Emitir NF-e junto no Asaas <span className="block text-[10px]">(requer CEP/endereço no cliente e Município configurado em Integrações)</span></span>
                      </label>
                      {pixData?.payload && (
                        <div className="space-y-2">
                          <img alt="QR PIX" src={`data:image/png;base64,${pixData.encodedImage}`} className="w-40 h-40 mx-auto border border-border rounded" />
                          <div className="flex items-center gap-2">
                            <Input readOnly value={pixData.payload} className="text-xs h-9" />
                            <Button size="icon" variant="outline" onClick={() => { navigator.clipboard.writeText(pixData.payload); toast.success("Copiado"); }}><Copy className="h-4 w-4" /></Button>
                          </div>
                        </div>
                      )}
                      {a.payment_invoice_url && (
                        <a className="text-xs underline text-primary" href={a.payment_invoice_url} target="_blank" rel="noreferrer">Abrir fatura no Asaas</a>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              )}
              {can("appointments.delete") && (
                <AlertDialog>
                  <AlertDialogTrigger asChild><Button variant="ghost" size="icon" data-testid={`btn-del-appt-${a.id}`} className="text-destructive"><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader><AlertDialogTitle>Remover agendamento?</AlertDialogTitle><AlertDialogDescription>Essa ação não pode ser desfeita.</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={() => remove(a.id)}>Remover</AlertDialogAction></AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!rescheduleFor} onOpenChange={(o) => !o && setRescheduleFor(null)}>
        <DialogContent data-testid="dialog-reschedule">
          <DialogHeader>
            <DialogTitle>Remarcar agendamento</DialogTitle>
            <DialogDescription>Escolha a nova data e hora. Se o horário estiver ocupado, você verá um aviso.</DialogDescription>
          </DialogHeader>
          {rescheduleFor && (
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground space-y-1">
                <div><span className="font-medium text-foreground">{rescheduleFor.client_name}</span></div>
                <div>{formatAppointmentServices(rescheduleFor)} · {rescheduleFor.professional_name} · {rescheduleFor.duration_minutes} min</div>
                <div>Atual: {new Date(rescheduleFor.scheduled_at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}</div>
              </div>
              <div className="space-y-2">
                <Label>Novo horário</Label>
                <Input
                  data-testid="input-reschedule-at"
                  type="datetime-local"
                  className="h-11"
                  value={rescheduleAt}
                  onChange={(e) => setRescheduleAt(e.target.value)}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRescheduleFor(null)} data-testid="btn-cancel-reschedule">Cancelar</Button>
            <Button onClick={confirmReschedule} disabled={rescheduling || !rescheduleAt} data-testid="btn-confirm-reschedule">
              {rescheduling ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <TollDialog appt={tollFor} finishing={tollFinishing} onClose={() => setTollFor(null)} onDone={() => { setTollFor(null); loadDay(); }} />
    </div>
  );
}
